import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {getFetch, postFetch} from '../../../ApiBackend';
import Grid from '@material-ui/core/Grid';
import BoxInfo from '../../../Components/BoxInfo';
import RedIconButton from '../../../core/components/buttons/RedIconButton';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDownload, faEdit, faSave} from '@fortawesome/free-solid-svg-icons';
import {useTheme} from '@material-ui/core';
import InactiveActiveSwitchState
from '../../../Components/InactiveActiveSwitchState';
import RedTextField from '../../../core/components/inputs/RedTextField';
import {useSelector} from 'react-redux';
import configs from '../../../configs';
import {displayError} from '../../../ui-components/displayMsg';
import fileDownload from 'js-file-download';

/**
 *
 * @param{string} service
 * @param{object} info
 * @param{function} setGlobalAutoStart
 * @param{boolean} globalAutoStart
 * @param{function} setUiVisibility
 * @param{boolean} uiVisibility
 * @return {JSX.Element}
 * @constructor
 */
export default function EndpointAgentInfoAndGlobalSettings(
    {service, info, setGlobalAutoStart, globalAutoStart, setUiVisibility, uiVisibility}) {
  const padding = useTheme().spacing(3);
  const [changeLicences, setChangeLicences] = useState(false);
  const [delta, setDelta] = useState('');
  const [submittingDelta, setSubmittingDelta] = useState(false);
  const [status, setStatus] = useState(info?.status ?? {});
  const [, keycloak] = useSelector((state) => [
    state.operator,
    state.keycloak,
  ]);
  const roles =
    keycloak.tokenParsed
        .resource_access[configs.KEYCLOAK_CONFIGS.clientId]?.roles || [];
  const canEdit = roles.includes('root') || roles.includes('admin');
  const canEditLicences = roles.includes('root');

  const onSaveDelta = (e) => {
    e.preventDefault();
    setSubmittingDelta(true);
    postFetch(`service/${service}/endpoint/licence`, {delta}).then((data) => {
      setSubmittingDelta(false);
      setChangeLicences(false);
      setDelta('');
      setStatus(data?.status ?? {});
    }).catch((e) => {
      e.text().then((data) => {
        setSubmittingDelta(false);
        displayError('Error adding bypass:', data);
      });
    });
  };

  const onChangeGlobalAutoStart = (globalAutoStart) => (_) => {
    postFetch(`service/${service}/endpoint/autoStart`,
        {autoStart: !globalAutoStart})
        .then(() => {
          setGlobalAutoStart(!globalAutoStart);
        });
  };

  const onChangeUiVisibility = (uiVisibility) => (_) => {
    postFetch(`service/${service}/endpoint/uiVisibility`,
        {uiVisibility: !uiVisibility})
        .then(() => {
          setUiVisibility(!uiVisibility);
        });
  };

  const handleDownload = () => {
    let fileName = 'agent.exe';
    getFetch(`service/${service}/endpoint/download`).then(async (response) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      fileName = contentDisposition ?
        JSON.parse(contentDisposition.split('filename=')[1]) :
        fileName;
      const fileHash = response.headers.get('X-File-Hash');
      const fileBlob = await response.blob();
      const arrayBuffer = await fileBlob.arrayBuffer();

      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map((b) => b.toString(16)
          .padStart(2, '0')).join('');
      if (hashHex !== fileHash) {
        displayError('Integrity error', 'the agent is corrupted.');
        return;
      }

      fileDownload(fileBlob, fileName);
    });
  };

  return <BoxInfo
    title={
      <b>Endpoint Agent Info & Global Settings</b>}>
    <Grid
      item
      xs={12}
      style={{
        paddingLeft: padding / 2,
        paddingRight: padding / 2,
      }}
    >
      <hr style={{opacity: 0}}/>
      <EndpointAgentGlobalVersion agentVersion={info.currentVersion}
        canEdit={canEdit}
        service={service}/>
    </Grid>
    <Grid
      item
      xs={12}
      style={{
        paddingLeft: padding / 2,
        paddingRight: padding / 2,
      }}
    >
      <hr/>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <span>Download newest Agent:</span>
        <RedIconButton TooltipProps={{title: 'Download Endpoint Agent'}}
          onClick={handleDownload}>
          <FontAwesomeIcon icon={faDownload}/>
        </RedIconButton>
      </div>
    </Grid>
    <Grid
      item
      xs={12}
      style={{
        paddingLeft: padding / 2,
        paddingRight: padding / 2,
      }}
    >
      <hr/>
      <p> DoH Proxy Ip: {info?.dohProxiesInfo?.ip ?? '-'}</p>
      <p> DoH Proxy Domain: {info?.dohProxiesInfo?.domain ?? '-'}</p>
    </Grid>
    <Grid
      item
      xs={12}
      style={{
        paddingLeft: padding / 2,
        paddingRight: padding / 2,
      }}
    >
      <hr/>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <span>Global Auto Start: &nbsp;</span>
        <InactiveActiveSwitchState
          disabled={!canEdit}
          checked={globalAutoStart}
          sideEffect={onChangeGlobalAutoStart(globalAutoStart)}
        />
      </div>
      <hr/>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <span>Ui Visibility: &nbsp;</span>
        <InactiveActiveSwitchState
          disabled={!canEdit}
          checked={uiVisibility}
          sideEffect={onChangeUiVisibility(uiVisibility)}
        />
      </div>
    </Grid>
    <Grid
      item
      xs={12}
      style={{
        paddingLeft: padding / 2,
        paddingRight: padding / 2,
      }}
    >
      <hr/>
      <div style={{display: 'flex', alignItems: 'center', height: '48px'}}>
        Licenses:&nbsp;
        <b>{status?.inUse || 0}</b>&nbsp;in use from {status?.total || 0} in
        total&nbsp;
        {canEditLicences && !changeLicences && <RedIconButton
          TooltipProps={{title: 'Change Licences'}}
          onClick={() => setChangeLicences(true)}
        >
          <FontAwesomeIcon icon={faEdit}/>
        </RedIconButton>}
        {changeLicences && <RedTextField
          variant="outlined"
          size="small"
          placeholder="Delta Licences"
          value={delta}
          disabled={submittingDelta}
          onChange={({target: {value}}) => setDelta(value)}
        > </RedTextField>}
        {changeLicences && delta && <RedIconButton
          TooltipProps={{title: 'Set Delta Licences'}}
          onClick={onSaveDelta}
        >
          <FontAwesomeIcon icon={faSave}/>
        </RedIconButton>}
      </div>
    </Grid>
  </BoxInfo>;
}

EndpointAgentInfoAndGlobalSettings.propTypes = {
  service: PropTypes.string,
  info: PropTypes.object,
  setGlobalAutoStart: PropTypes.func,
  globalAutoStart: PropTypes.bool,
  uiVisibility: PropTypes.bool,
  setUiVisibility: PropTypes.func,
};

/**
 * Component for displaying and editing the global agent version.
 *
 * @param {Object} props
 * @param {boolean} props.canEdit - Whether the user can edit the version.
 * @param {string} props.agentVersion - The current agent version.
 * @param {string} props.service - The service identifier.
 * @return {JSX.Element}
 */
function EndpointAgentGlobalVersion({canEdit, agentVersion, service}) {
  const [version, setVersion] = useState(agentVersion);
  const [changeAgentVersion, setChangeAgentVersion] = useState(false);
  const [latestVersion, setNewVersion] = useState('');
  const [submittingNewVersion, setSubmittingNewVersion] = useState(false);

  const onSaveGlobalAgentVersion = (e) => {
    e.preventDefault();
    setSubmittingNewVersion(true);
    postFetch(`service/${service}/endpoint/latestVersion`,
        {latestVersion}).then(() => {
      setSubmittingNewVersion(false);
      setChangeAgentVersion(false);
      setNewVersion('');
      setVersion(latestVersion);
    }).catch((e) => {
      e.text().then((data) => {
        setSubmittingNewVersion(false);
        displayError('Error setting the global version:', data);
      });
    });
  };

  return <div style={{display: 'flex', alignItems: 'center'}}>
    <span>Current Version:&nbsp;</span>
    <span>{version}</span>

    {canEdit && !changeAgentVersion && <RedIconButton
      TooltipProps={{title: 'Change Licences'}}
      onClick={() => setChangeAgentVersion(true)}
    >
      <FontAwesomeIcon icon={faEdit}/>
    </RedIconButton>}
    {changeAgentVersion && <RedTextField
      variant="outlined"
      size="small"
      placeholder="New Version"
      value={latestVersion}
      disabled={submittingNewVersion}
      onChange={({target: {value}}) => setNewVersion(value)}
    > </RedTextField>}
    {changeAgentVersion && latestVersion && <RedIconButton
      TooltipProps={{title: 'Set New Version'}}
      onClick={onSaveGlobalAgentVersion}
    >
      <FontAwesomeIcon icon={faSave}/>
    </RedIconButton>}
  </div>;
}

EndpointAgentGlobalVersion.propTypes = {
  canEdit: PropTypes.bool,
  agentVersion: PropTypes.string,
  service: PropTypes.string,
};
